// Caregiver-facing "this phone needs attention" banner.
//
// A loved one's phone can silently lose the background-protection settings that
// keep Find Phone + walk tracking working (battery optimization, Physical
// activity permission, Data Saver, auto-revoke). The elderly user won't notice,
// so the phone self-reports its posture (~hourly) and this banner surfaces any
// regression to the caregiver on the dashboard. See PWA_HANDOFF_device_health.md.
//
// Display-only: the fix happens on the phone (mobile setup gate / Profile →
// Phone health). Hidden when everything is healthy and the report is fresh.

import { useEffect, useState } from "react"
import { AlertTriangle } from "lucide-react"
import { DataService } from "../services/data.service"
import { DeviceHealth, evaluateDeviceHealth } from "../models/deviceHealth.model"

export function DeviceHealthBanner({ beneficiaryUserId }: { beneficiaryUserId: number | null }) {
    const [dh, setDh] = useState<DeviceHealth | null>(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (beneficiaryUserId == null) return
        let alive = true
        DataService.getDeviceHealth(beneficiaryUserId).then(r => {
            if (alive) { setDh(r); setLoaded(true) }
        })
        return () => { alive = false }
    }, [beneficiaryUserId])

    if (!loaded || !dh) return null
    const { issues, hasCritical, stale } = evaluateDeviceHealth(dh)
    if (issues.length === 0 && !stale) return null

    const tone = hasCritical
        ? { box: 'bg-red-50 border-red-300', title: 'text-red-800', text: 'text-red-700', icon: 'text-red-600' }
        : { box: 'bg-amber-50 border-amber-300', title: 'text-amber-800', text: 'text-amber-700', icon: 'text-amber-600' }

    return (
        <div className={`p-4 rounded-lg border shadow-md ${tone.box}`} id="device-health">
            <div className="flex items-start space-x-3">
                <AlertTriangle className={`mt-0.5 shrink-0 ${tone.icon}`} size={22} />
                <div className="space-y-1">
                    <h3 className={`text-sm font-semibold ${tone.title}`}>This phone needs attention</h3>

                    {issues.length > 0 ? (
                        <>
                            <p className={`text-xs ${tone.text}`}>
                                {hasCritical
                                    ? 'Find Phone and walk tracking may not work until these are fixed on the phone:'
                                    : 'Recommended settings to check on the phone:'}
                            </p>
                            <ul className={`text-xs list-disc list-inside ${tone.text}`}>
                                {issues.map((iss, i) => <li key={i}>{iss.label}</li>)}
                            </ul>
                        </>
                    ) : (
                        <p className={`text-xs ${tone.text}`}>
                            This phone hasn’t checked in recently — it may be off or the app was removed.
                        </p>
                    )}

                    <p className={`text-[11px] ${tone.text} opacity-80`}>
                        Fix on the phone: open WellNuo → Profile → Phone health, or Settings → Apps → WellNuo → Permissions / Battery.
                    </p>
                </div>
            </div>
        </div>
    )
}
